/**
 * Which query parameters mean "this URL names the ITEM LIST".
 *
 * Spelled once, here, because two very different places have to agree about
 * it and a copy in each was the bug:
 *
 *  - the server entry route resolves the verified company URL (or `/home`
 *    for a personal workspace) before serving the console;
 *  - `src/client/shell/Shell.tsx` decides which page a URL shows.
 *
 * A page-less URL that carries item-list state is a bookmark from before the
 * Home landing existed — `/?view=task&sort=oldest` — and it must still open
 * the list it named. The server redirect carried the query along to `/home`,
 * where the clean path then said "Home" louder than the parameters said
 * "list", so those bookmarks silently lost their filter. Both sides now ask
 * the same question of the same list.
 *
 * `item` and `walk` are here for the same reason: they name a record or a
 * walk to OPEN, so a URL carrying one is not a request for Home either.
 */

/** Item-list state: the saved view, the grouping, the sort, the filters. */
export const ITEM_LIST_PARAMS = ["view", "groupBy", "sort", "stalledOnly", "severity"] as const;

/** A record or walk the URL asks to open over whatever page it names. */
export const OPEN_TARGET_PARAMS = ["item", "walk"] as const;

/**
 * Everything else that makes a URL more than "the bare host".
 *
 * `page` is the obvious one and was missed the first time: `/?page=review`
 * was redirected to `/home?page=review`, which still SHOWED the review queue
 * (the parameter wins over the path) but moved a bookmark onto a path that
 * says Home. `project` and `reviewing` name what a page is about, so they
 * belong here for the same reason.
 */
export const PAGE_STATE_PARAMS = ["page", "project", "reviewing"] as const;

/**
 * True when this query names something other than "where do things stand" —
 * item-list state, or a record to open.
 *
 * Accepts anything `URLSearchParams` can be built from, so a caller may pass
 * `location.search`, a `URL`'s `searchParams`, or a bare query string.
 */
export function namesSomethingOtherThanHome(
  query: URLSearchParams | string,
): boolean {
  const params = typeof query === "string" ? new URLSearchParams(query) : query;
  return [...ITEM_LIST_PARAMS, ...OPEN_TARGET_PARAMS, ...PAGE_STATE_PARAMS].some(
    (key) => params.get(key) !== null,
  );
}

/** Preserve legacy Home/list intent while moving to a verified company URL. */
export function companyEntryPath(url: URL, slug: string, mount: string): string {
  const params = new URLSearchParams(url.search);
  params.delete("workspace");
  params.delete("company");
  params.delete("authToken");
  if (/\/home\/?$/.test(url.pathname) && !params.has("page") && ITEM_LIST_PARAMS.some(key => params.has(key))) {
    params.set("page", "brief");
  }
  return `${mount}/${encodeURIComponent(slug)}${params.size ? `?${params}` : ""}${url.hash}`;
}
